import {
  IonAlert,
  IonBadge,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonNote,
  IonSpinner,
  IonText,
  IonToggle,
} from '@ionic/react';
import { warningOutline } from 'ionicons/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useIsLifetimeMember } from '../../hooks/useIsLifetimeMember';
import ReleaseChannelService, {
  ArchivedVersion,
  ReleaseChannelState,
  VersionArchive,
} from '../../services/ReleaseChannel.service';
import isElectron from '../../utils/isElectron';
import './ReleaseChannelSettings.scss';

const formatReleaseDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const ReleaseChannelSettings: React.FC = () => {
  const isLifetimeMember = useIsLifetimeMember();

  const [state, setState] = useState<ReleaseChannelState | null>(null);
  const [archive, setArchive] = useState<VersionArchive | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingVersion, setPendingVersion] = useState<ArchivedVersion | null>(
    null,
  );

  const load = useCallback(async () => {
    setLoading(true);
    const [nextState, nextArchive] = await Promise.all([
      ReleaseChannelService.getState(),
      ReleaseChannelService.getArchive(),
    ]);
    setState(nextState);
    setArchive(nextArchive);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isElectron()) {
      void load();
    } else {
      setLoading(false);
    }
  }, [load]);

  const handleChannelToggle = useCallback(
    async (enabled: boolean) => {
      setBusy(true);
      setError(null);
      try {
        await ReleaseChannelService.setChannel(
          enabled ? 'lifetime' : 'production',
        );
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  const handleInstallVersion = useCallback(
    async (version: string | null) => {
      setBusy(true);
      setError(null);
      const result = await ReleaseChannelService.installVersion(version);
      if (!result.ok) {
        setError(
          result.message ??
            'Could not start that version. It may no longer be available.',
        );
      }
      await load();
      setBusy(false);
    },
    [load],
  );

  if (!isElectron()) {
    return null;
  }

  if (loading) {
    return (
      <div className="release-channel-settings">
        <IonSpinner name="dots" />
      </div>
    );
  }

  const onLifetime = state?.channel === 'lifetime';
  const pinnedVersion = state?.pinnedVersion ?? null;
  const versions = archive?.versions ?? [];

  // Dev and Next builds carry their own app identity, so pointing them at the
  // lifetime feed would install a second copy of the app rather than update
  // this one. Those testers stay on their own channel.
  const canSwitchChannel =
    !!state && state.switchableChannels.includes(state.channel);

  return (
    <div className="release-channel-settings">
      {isLifetimeMember && canSwitchChannel && (
        <div className="release-channel-block">
          <IonItem lines="none" className="release-channel-toggle">
            <IonLabel>
              <h3>Early access builds</h3>
              <IonNote>
                As a lifetime member you can receive releases before they go out
                to everyone. You will still get every general release as soon as
                it ships.
              </IonNote>
            </IonLabel>
            <IonToggle
              checked={onLifetime}
              disabled={busy}
              onIonChange={(event) => handleChannelToggle(event.detail.checked)}
            />
          </IonItem>
          {onLifetime && (
            <IonText color="warning" className="release-channel-warning">
              <IonIcon icon={warningOutline} />
              <span>
                Early access builds are tested less than general releases and
                can be ahead of what the Crate Hackers service expects. If
                something breaks, turn this off and you will move back to the
                general release.
              </span>
            </IonText>
          )}
        </div>
      )}

      {versions.length > 0 && (
        <div className="release-channel-block">
          <h3 className="release-channel-heading">Previous versions</h3>
          <IonNote className="release-channel-subheading">
            If a recent update caused a problem you can go back to an earlier
            build while it is fixed.
          </IonNote>

          <IonText color="warning" className="release-channel-warning">
            <IonIcon icon={warningOutline} />
            <span>
              Older builds are kept working with the current Crate Hackers
              service for a limited window only. Features added since the
              version you pick will be missing, and anything relying on newer
              service behaviour may not work. Going back to the latest version
              always fixes it.
            </span>
          </IonText>

          <ul className="release-channel-versions">
            {versions.map((entry) => {
              const isCurrent = entry.version === state?.currentVersion;
              return (
                <li key={entry.version}>
                  <div className="version-meta">
                    <span className="version-number">{entry.version}</span>
                    {entry.prerelease && (
                      <IonBadge color="warning">Early access</IonBadge>
                    )}
                    {isCurrent && (
                      <IonBadge color="success">Installed</IonBadge>
                    )}
                    <IonNote>{formatReleaseDate(entry.releaseDate)}</IonNote>
                  </div>
                  <IonButton
                    fill="outline"
                    size="small"
                    disabled={busy || isCurrent}
                    onClick={() => setPendingVersion(entry)}
                  >
                    Install
                  </IonButton>
                </li>
              );
            })}
          </ul>

          {pinnedVersion && (
            <div className="release-channel-pinned">
              <IonNote>
                Updates are paused so you stay on {pinnedVersion}.
              </IonNote>
              <IonButton
                fill="clear"
                size="small"
                disabled={busy}
                onClick={() => handleInstallVersion(null)}
              >
                Resume updates
              </IonButton>
            </div>
          )}
        </div>
      )}

      {error && (
        <IonText color="danger" className="release-channel-error">
          {error}
        </IonText>
      )}

      <IonAlert
        isOpen={!!pendingVersion}
        header={`Install version ${pendingVersion?.version ?? ''}?`}
        message={
          'The app will download this version and restart to install it. Updates stay paused until you resume them, so you will not be moved forward again automatically.'
        }
        buttons={[
          { text: 'Cancel', role: 'cancel' },
          {
            text: 'Install',
            handler: () => {
              const version = pendingVersion?.version ?? null;
              void handleInstallVersion(version);
            },
          },
        ]}
        onDidDismiss={() => setPendingVersion(null)}
      />
    </div>
  );
};

export default ReleaseChannelSettings;
